import React, { ReactElement, useEffect, useMemo, useState } from "react";
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from "../../../../messages/en-US";
import moment from "moment";
import { SortDescriptor, process, State } from "@progress/kendo-data-query";
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import { Grid, GridColumn as Column, GridToolbar} from "@progress/kendo-react-grid";
import { CustomTextFilterCell } from "../../../Shared/Components/Table/CustomTextFilterCell";
import { Button } from "@progress/kendo-react-buttons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { downloadIcon } from "@progress/kendo-svg-icons";

interface Props {
  events: Array<any>;
  aggregator: string;
  legendConfig: any;
  isMultiple?: boolean; // Whether multiple endpoints are selected
}

interface Page {
  skip: number;
  take: number;
}

const page: Page = { skip: 0, take: 30 };

export default function EndpointFlowMeterTableAssets(props: Props): ReactElement {
  const localizationService = useLocalization();
  const aggregator = props.aggregator;
  const isMultiple = props.isMultiple || false;
  const initialSort: Array<SortDescriptor> = [{ field: "timestamp", dir: "desc" }];
  const [sort] = React.useState(initialSort);
  const _export = React.useRef<ExcelExport | null>(null);
  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };

  //Creating dataState to prevent issues with virtual scrolling
  const createAppState = (dataState: State) => {
    return {
      result: process(props.events, dataState),
      dataState: dataState,
    };
  };

  const [dataState, setDataState] = useState<any>(
    createAppState({
      skip: page.skip,
      take: page.take,
      sort: sort
    })
  );
  
  const dataStateChange = (event) => {
    setDataState(createAppState(event.dataState));
  };

  useEffect(() => {
    setDataState( createAppState({
      skip: page.skip,
      take: page.take,
      sort: sort
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  //Formating Cells
  const OccurredCell = (props: any) => {
    return (
      <td>
        {aggregator === 'none' ? 
          <div>{moment(props.dataItem.timestamp).format('DD/MM/YYYY @ HH:mm:ss')}</div>
          :
          <div>{moment(props.dataItem.timestamp).format('DD/MM/YYYY')}</div>
        }
      </td>
    );
  }

  const VolumeCell = (props: any) => {
    return (
      <td>
        <div>{props.dataItem.volume !== undefined && props.dataItem.volume !== null ? props.dataItem.volume.toFixed(2) : '-'}</div>
      </td>
    );
  }

  const VolumeTempCompCell = (props: any) => {
    return (
      <td>
        <div>{props.dataItem.volumeTemperatureCompensated !== undefined && props.dataItem.volumeTemperatureCompensated !== null ? props.dataItem.volumeTemperatureCompensated.toFixed(2) : '-'}</div>
      </td>
    );
  }

  const FlowRateCell = (props: any) => {
    return (
      <td>
        <div>{props.dataItem.flowRate !== undefined && props.dataItem.flowRate !== null ? props.dataItem.flowRate.toFixed(2) : '-'}</div>
      </td>
    );
  }

  const FlowRateTempCompCell = (props: any) => {
    return (
      <td>
        <div>{props.dataItem.flowRateTemperatureCompensated !== undefined && props.dataItem.flowRateTemperatureCompensated !== null ? props.dataItem.flowRateTemperatureCompensated.toFixed(2) : '-'}</div>
      </td>
    );
  }

  const TemperatureCell = (props: any) => {
    return (
      <td>
        <div>{props.dataItem.temperature !== undefined && props.dataItem.temperature !== null ? props.dataItem.temperature.toFixed(2) : '-'}</div>
      </td>
    );
  }

  const EndpointNameCell = (props: any) => {
    return (
      <td>
        <div>{props.dataItem.endpointName || props.dataItem.endpointId || '-'}</div>
      </td>
    );
  }

  const exportData = useMemo(() => {
    return props.events.map((item) => ({
      ...item,
      timestampExport: props.aggregator === "none" ? moment(item.timestamp).format('DD/MM/YYYY HH:mm:ss') : moment(item.timestamp).format('DD/MM/YYYY'),
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.events]);

  const getExcelExportData = () => {
    return process(exportData, {filter: dataState.dataState.filter, sort: dataState.dataState.sort}).data;
  };
  
  return (
    <div>
      <ExcelExport data={getExcelExportData()} ref={_export} collapsible={true} fileName={`Flow Meter Events.xlsx`}>
        <ExcelExportColumn field="timestampExport" title={`${localizationService.toLanguageString('custom.occurred', enMessages.custom.occurred)}`} width={150}/>
        {isMultiple && <ExcelExportColumn field="endpointName" title={`${localizationService.toLanguageString('custom.name', enMessages.custom.name)}`} />}
        <ExcelExportColumn field="volume" title="Volume (L)" cellOptions={{ format: "0.0000" }} hidden={!props.legendConfig.volume} />
        <ExcelExportColumn field="volumeTemperatureCompensated" title="Volume (Temp Comp) (L)" cellOptions={{ format: "0.0000" }} hidden={!props.legendConfig.volumeTemperatureCompensated} />
        <ExcelExportColumn field="flowRate" title={`${localizationService.toLanguageString('custom.flowRate', enMessages.custom.flowRate)} (L/min)`} cellOptions={{ format: "0.0000" }} hidden={!props.legendConfig.flowRate} />
        <ExcelExportColumn field="flowRateTemperatureCompensated" title="Flow Rate (Temp Comp) (L/min)" cellOptions={{ format: "0.0000" }} hidden={!props.legendConfig.flowRateTemperatureCompensated} />
        <ExcelExportColumn field="temperature" title={`${localizationService.toLanguageString('custom.temperature', enMessages.custom.temperature)} (°C)`} cellOptions={{ format: "0.0000" }} hidden={!props.legendConfig.temperature} />
      </ExcelExport>
      <Grid
        key={props.events.length.toString()}
        rowHeight={46}
        take={page.take}
        data={dataState.result}
        scrollable={"virtual"}
        filterable={true}
        onDataStateChange={dataStateChange}
        {...dataState.dataState}
        sortable={true}
        style={{
          height: `calc(100vh - 330px)`,
        }}
        className=" w-full z-0 extra-filter-button activity-table mt-5 "
      >
        <GridToolbar>  
          <div style={{width:'100%', display:'flex', justifyContent:'flex-end'}}>
            <Tooltip anchorElement="target" position="bottom">
              <Button
                title={localizationService.toLanguageString('custom.exportToExcel', enMessages.custom.exportToExcel)}
                svgIcon={downloadIcon}
                disabled={dataState.result.data.length === 0}
                onClick={excelExport}
              />
            </Tooltip>
          </div>
        </GridToolbar>
        <Column field="timestamp" title={`${localizationService.toLanguageString('custom.occurred', enMessages.custom.occurred)}`} cell={OccurredCell} filterable={false}/>
        {isMultiple && <Column field="endpointName" title={`${localizationService.toLanguageString('custom.name', enMessages.custom.name)}`} filterCell={CustomTextFilterCell} cells={{data: EndpointNameCell}}/>}
        <Column field="volume" title="Volume (L)" filterCell={CustomTextFilterCell} hidden={!props.legendConfig.volume} cells={{data: VolumeCell}}/>
        <Column field="volumeTemperatureCompensated" title="Volume (Temp Comp) (L)" filterCell={CustomTextFilterCell} hidden={!props.legendConfig.volumeTemperatureCompensated} cells={{data: VolumeTempCompCell}}/>
        <Column field="flowRate" title={`${localizationService.toLanguageString('custom.flowRate', enMessages.custom.flowRate)} (L/min)`} filterCell={CustomTextFilterCell} hidden={!props.legendConfig.flowRate} cells={{data: FlowRateCell}}/>
        <Column field="flowRateTemperatureCompensated" title="Flow Rate (Temp Comp) (L/min)" filterCell={CustomTextFilterCell} hidden={!props.legendConfig.flowRateTemperatureCompensated} cells={{data: FlowRateTempCompCell}}/>
        <Column field="temperature" title={`${localizationService.toLanguageString('custom.temperature', enMessages.custom.temperature)} (°C)`} filterCell={CustomTextFilterCell} hidden={!props.legendConfig.temperature} cells={{data: TemperatureCell}}/>
      </Grid>     
    </div>
  );
}
